<!-- eslint-disable vue/no-deprecated-v-bind-sync -->
<template>
  <v-row class="mb-2">
    <v-col cols="7" class="mx-n4 mt-1">
      <v-card-title>Épingler un produit</v-card-title>
    </v-col>
    <v-col cols="8" class="mt-n8">
      <v-switch
        :input-value="actionValue.availableSearchProduct"
        class="switcher"
        :label="actionValue.availableSearchProduct ? 'Product available on search' : 'Product by iresa code'"
        @change="changeProductSearchMethod"
      />
    </v-col>
    <v-col v-if="actionValue.availableSearchProduct" cols="8" class="mt-n8">
      <v-autocomplete
        v-model="actionValue.item"
        :search-input.sync="queryProductSearch"
        :hide-no-data="loading || items.length === 0"
        :loading="loading"
        :item-text="getFieldText"
        item-value="objectID"
        item-color="info"
        menu-props="light"
        chips
        label="Sélectionner un item"
        :items="items"
        no-filter
        color="blue"
        clearable
        :rules="[v => !!v || 'Required'] "
        return-object
      >
        <template #item="{ item }">
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
            <v-list-item-subtitle>{{ item.package }} / {{ item.uuid }} / {{ item.objectID }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </v-col>

    <v-col v-else cols="8" class="mt-n8">
      <v-autocomplete
        v-model="actionValue.item"
        :search-input.sync="queryIresaSearch"
        :hide-no-data="loading || itemsWithIresaCode.length === 0"
        :loading="loading"
        item-value="uuid"
        item-color="info"
        menu-props="light"
        item-text="iresaPackageCode"
        :items="itemsWithIresaCode"
        chips
        label="Sélectionner un item"
        no-filter
        color="blue"
        clearable
        :rules="[v => !!v || 'Required'] "
        return-object
      >
        <template #item="{ item }">
          <v-list-item-content>
            <v-list-item-title>{{ item.wtitle }}</v-list-item-title>
            <v-list-item-subtitle>Iresa package code: {{ item.iresaPackageCode }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </v-col>
    <v-col />
    <v-col cols="2" class="mx-8 mt-n8">
      <v-text-field
        v-model.number="actionValue.position"
        color="blue"
        type="number"
        :rules="[isPositiveInteger]"
        label="Position"
      />
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vue2-helpers/vuex';

import { useApolloClient } from '@vue/apollo-composable';

import { getGraphqlSchema } from '@/graphql/index';

const { isPositiveInteger } = require('@/utils');
const { openSearchLiveContentFilters } = require('@/utils/merch');

const store = useStore();
const { resolveClient } = useApolloClient();

const props = defineProps({ action: { type: Object, required: true } });

const actionValue = ref(props.action);

const queryProductSearch = ref(null);
const queryIresaSearch = ref(null);
const products = ref([]);
const productsIresaCode = ref([]);

const loading = ref(false);

const changeProductSearchMethod = val => {
  actionValue.value.availableSearchProduct = !!val;
  actionValue.value.item = null;
};

const getFieldText = item => `${item.name} | ${item.uuid}`;

const items = computed(() => {
  if (actionValue.value.item) {
    return products.value.concat([{
      objectID: actionValue.value.item.objectID,
      name: actionValue.value.item.name,
      uuid: actionValue.value.item.uuid,
      context: actionValue.value.item.context
    }]);
  }

  return products.value;
});

let observable = null;

const searchAlgolia = search => {
  loading.value = true;
  products.value = [];

  const contextFilters = store.getters.contexAndAgencyFilters;

  if (observable) {
    observable.unsubscribe();
  }

  const apollo = resolveClient();
  const watchedQuery = apollo.watchQuery({
    query: getGraphqlSchema('searchAlgolia'),
    variables: {
      query: search || '',
      args: {
        enableRules: false,
        filters: contextFilters || '',
        attributesToRetrieve: [
          'name',
          'uuid',
          'context',
          'package'
        ]
      }
    }
  });

  // Subscribe to it, and do something with the data
  observable = watchedQuery.subscribe(({ data }) => {
    products.value = data.searchAlgolia.items.map(i => ({
      uuid: i.uuid,
      name: i.name,
      package: i.package,
      objectID: i.objectID,
      context: i.context
    }));
    loading.value = false;
  });
};

// Switch to open search
const searchProductIresaCode = query => {
  loading.value = true;
  productsIresaCode.value = [];

  const filters = { query: { bool: { filter: openSearchLiveContentFilters() } } };

  filters.query.bool.filter.push({
    terms: {
      wtype: [
        'product'
      ]
    }
  });

  filters.query.bool.must = {
    simple_query_string: {
      query: `${query}*`,
      fields: [
        'wtitle',
        'iresaPackageCode',
        'iresaCode',
        'uuid'
      ]
    }
  };

  if (observable) {
    observable.unsubscribe();
  }

  const apollo = resolveClient();
  const watchedQuery = apollo.watchQuery({
    query: getGraphqlSchema('openSearch'),
    variables: {
      input: {
        from: 0,
        size: 1000,
        query: filters.query
      }
    },
    context: { headers: { mode: 'live' } }
  });

  // Subscribe to it, and do something with the data
  observable = watchedQuery.subscribe(({ data }) => {
    productsIresaCode.value = data.search.items.map(i => ({
      uuid: i.uuid,
      iresaPackageCode: i.iresaPackageCode || i.iresaCode,
      wtitle: i.wtitle
    }));
    loading.value = false;
  });
};

const itemsWithIresaCode = computed(() => {
  if (actionValue.value.item) {
    return productsIresaCode.value.concat([{
      iresaPackageCode: actionValue.value.item.iresaPackageCode,
      wtitle: actionValue.value.item.wtitle,
      uuid: actionValue.value.item.uuid
    }]);
  }

  return productsIresaCode.value;
});

watch(() => queryProductSearch.value, val => {
  searchAlgolia(val);
});

watch(() => queryIresaSearch.value, val => {
  searchProductIresaCode(val);
});

if (!actionValue.value.position) {
  actionValue.value.position = 1;
}

if (actionValue.value && !actionValue.value.hasOwnProperty('availableSearchProduct')) {
  actionValue.value.availableSearchProduct = true;
}

</script>

<style>
</style>
